import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom, retry } from 'rxjs';
import { SERVER_URL, environment } from 'src/environments/environment';
import { Flow } from '../interface/flow';
import { StorageService } from './storage.service';
import { ToolsService } from './tools.service';
import { PUBLIC_LOGIN_REQUEST } from '../interceptors/auth-context';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userPermissionsMock: string[] = [
    'invoices.approve',
    'invoices.list',
    'clients.list',
    'reports.invoices',
    'reports.points',
    'reports.list',
    'communications.send',
  ];
  public showSideBar: boolean = false;
  currentMall: any;
  malls: Array<any> = [];
  user: any;
  tokenClientUploadBill!: string;

  constructor(
    public http: HttpClient,
    private storageService: StorageService,
    private toolsService: ToolsService,
  ) {}

  setToken(token: string) {
    this.tokenClientUploadBill = token;
  }

  saveLiteToken(token: string): void {
    window.sessionStorage.removeItem('lite_token');
    window.sessionStorage.setItem('lite_token', token);
  }

  login(email: string, password: string, captchaToken: string) {
    const url = environment.url + '/anubis/auth/login/admin';
    const body = {
      email: email,
      password: password,
      captcha_token: captchaToken,
    };
    return lastValueFrom(this.http.post(url, body)).then(
      async (response: any) => {
        this.storageService.saveToken(response.access_token);
        this.storageService.saveRefreshToken(response.refresh_token);
        await this.getUserInfo();
        await this.getUserPermission();
      },
    );
  }

  getTenantByCode(code: string) {
    const url = `${environment.url}/osiris/customer/code/${encodeURIComponent(code)}/`;
    return lastValueFrom(this.http.get(url, {
      context: new HttpContext().set(PUBLIC_LOGIN_REQUEST, true),
    }));
  }

  getTenantLoginOptions(customerId: number) {
    const url = `${environment.url}/anubis/auth/identity-providers/customer/${customerId}/login-options`;
    return lastValueFrom(this.http.get(url, {
      context: new HttpContext().set(PUBLIC_LOGIN_REQUEST, true),
    }));
  }

  getAzureAdLoginUrl(customerId: number, customerCode: string): string {
    const params = new URLSearchParams({
      customer_id: String(customerId),
      customer_code: customerCode,
    });
    return `${environment.url}/anubis/auth/identity-providers/azure-ad/login?${params.toString()}`;
  }

  loginWithIdentityProvider(ticket: string) {
    const url = `${environment.url}/anubis/auth/identity-providers/login/exchange`;
    return lastValueFrom(this.http.post(url, { ticket }, {
      context: new HttpContext().set(PUBLIC_LOGIN_REQUEST, true),
    })).then(
      async (response: any) => {
        this.storageService.saveToken(response.access_token);
        this.storageService.saveRefreshToken(response.refresh_token);
        await this.getUserInfo();
        await this.getUserPermission();
      },
    );
  }

  refreshToken(token?: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json; version=ClientesV1',
        Authorization: 'Bearer ' + window.localStorage.getItem('refresh_token'),
      }),
    };
    const url = environment.url + '/anubis/auth/refresh';
    return this.http.post(url, {}, httpOptions);
  }

  setCurrentMall(mall: any) {
    this.loadMall(mall.id);
    this.storageService.saveSelectedMall(mall);
    window.sessionStorage.removeItem('location_madatory');
    window.sessionStorage.setItem('location_madatory', mall.location_madatory);
    this.currentMall = mall;
    this.getUserPermission();
    console.debug('setCurrentMall', mall);
  }

  // loadMall(mallId: number) {
  //   this.http
  //     .get(`${environment.url}/osiris/customer/${mallId}/`)
  //     .toPromise()
  //     .then((response: any) => {
  //       this.currentMall = response;
  //       console.log(this.currentMall)
  //       this.storageService.saveSelectedMall(response);
  //       this.toolsService.removeInjectedScripts();
  //       for (let script of response.admin_scripts) {
  //         this.toolsService.addScript(script);
  //       }
  //     });
  // }

  loadMall(mallId: number): Promise<any> {
    return this.http
      .get(`${environment.url}/osiris/customer/${mallId}/`)
      .toPromise()
      .then((response: any) => {
        this.currentMall = response;
        this.storageService.saveSelectedMall(response);
        this.toolsService.removeInjectedScripts();

        for (const script of response.admin_scripts || []) {
          this.toolsService.addScript(script);
        }

        return response;
      });
  }

  setUserData(user: any) {
    this.user = user;
    this.loadMalls();
  }

  loadMalls() {
    if (!this.user.customers) return;

    this.malls = this.user.customers;
    let selectedMall = this.storageService.getSelectedMall();
    if (selectedMall) {
      // If the selected mall is in the user malls, set it as the current mall
      let mallAdmin = this.malls.find((mall) => mall.id == selectedMall.id);
      console.debug(
        'loadMalls mallAdminFound',
        mallAdmin,
        selectedMall.id,
        this.malls,
      );
      if (mallAdmin) {
        this.setCurrentMall(selectedMall);
        return;
      }
    }
    this.setCurrentMall(this.malls[0]);
  }

  getUserInfo(token?: string) {
    const url = environment.url + '/anubis/auth/user';
    const httpOptions = this.storageService.getAuthHeaders();
    return lastValueFrom(this.http.get(url, httpOptions)).then(
      (response: any) => {
        this.setUserData(response);
        this.storageService.saveUser(response);

        return response;
      },
    );
  }

  getUserInfoLite(token?: string) {
    const url = environment.url + '/anubis/auth/user';
    const httpOptions = this.storageService.getAuthHeaders();
    return lastValueFrom(this.http.get(url, httpOptions)).then(
      (response: any) => {
        return response;
      },
    );
  }

  getUserPermission(id: number = 0) {
    id = this.storageService.getSelectedMall().id;
    const url =
      environment.url + '/anubis/roles/permission/customer?customer=' + id;
    const httpOptions = this.storageService.getAuthHeaders();
    return lastValueFrom(this.http.get(url, httpOptions)).then(
      (response: any) => {
        this.userPermissionsMock = response;
        this.storageService.savePermission(response);
        return response;
      },
    );
  }

  getClientsList(limit: number, offset: number, filters?: any) {
    let url = `${environment.url}/horus/crm/profile/list?limit=${limit}&offset=${offset}`;

    let params: any = {
      customer: this.storageService.getSelectedMall()?.id,
    };

    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach((key) => {
        params[key] = filters[key] !== undefined ? filters[key] : '';
      });
    }

    const headers = this.storageService.getSimpleAuthHeaders();
    return this.http.get(url, { params, headers }).toPromise();
  }

  getClientsListData(all: boolean = true) {
    let url = environment.url + '/horus/crm/profile/fields?all=' + all;
    let headers = this.storageService.getSessionAuthHeaders();

    return this.http.get(url, headers).toPromise();
  }

  getProfileInfoByIds(ids: any, limit?: any, offset?: any) {
    let url = `${environment.url}/horus/crm/profile/list?user_id=${ids}&limit=${limit}`;
    const params = {
      customer: this.storageService.getSelectedMall()?.id,
    };
    const headers = this.storageService.getSimpleAuthHeaders();
    return this.http.get(url, { params, headers }).toPromise();
  }

  getProfileAdminsInfoByIds(ids: any) {
    let url = `${environment.url}/anubis/auth/user/name/${ids}`;
    const params = {
      customer: this.storageService.getSelectedMall()?.id,
    };
    const headers = this.storageService.getSimpleAuthHeaders();
    return this.http.get(url, { params, headers }).toPromise();
  }

  getOneProfile(id: number) {
    sessionStorage.setItem('user_logued_id', id.toString());
    let url = `${environment.url}/horus/crm/profile/${id}`;
    const headers = this.storageService.getAuthHeaders();
    return this.http.get(url, headers).toPromise();
  }

  getUserLoguedId(): number | null {
    const id = sessionStorage.getItem('user_logued_id');
    return id ? Number(id) : null;
  }

  clearUserLoguedId(): void {
    sessionStorage.removeItem('user_logued_id');
  }

  async isLogged() {
    // If the user is loaded, return true
    if (this.user) {
      this.showSideBar = true;
      return true;
    }
    if (!this.storageService.getAuthToken()) {
      this.showSideBar = false;
      return false;
    }
    try {
      let user = await this.getUserInfo();
    } catch (error) {
      console.debug('Error', error);
      this.showSideBar = false;
      return false;
    }
    this.showSideBar = true;
    return true;
  }

  logout() {
    this.user = null;
    this.malls = [];
    this.currentMall = null;
    this.showSideBar = false;
    this.storageService.clean();
    const url = SERVER_URL + '/crm/logout/';
    const httpOptions = this.storageService.getAuthHeaders();
    this.http.post(url, {}, httpOptions);
  }

  hasPermission(permission: string): boolean {
    this.userPermissionsMock = this.storageService.getPermission();
    return (
      this.userPermissionsMock.length > 0 &&
      this.userPermissionsMock.includes(permission)
    );
  }

  //get the user bills by ids
  userBills(ids: any) {
    let url = `${environment.url}/thoth/bills/user-summary?user_ids=${ids}`;
    const headers = this.storageService.getAuthHeaders();
    return this.http.get(url, headers).toPromise();
  }

  updateUser(id: number, data: any) {
    const url = environment.url + '/horus/crm/profile/' + id + '/update';
    const httpOptions = this.storageService.getAuthHeaders();
    return lastValueFrom(this.http.put(url, data, httpOptions)).then(
      (response: any) => {
        return response;
      },
    );
  }

  updateIncrementalInfo(client_id: any, data: any) {
    let url = environment.url + '/horus/crm/answers/update';
    let httpOptions = this.storageService.getSessionAuthHeaders();
    let body = {
      user_id: client_id,
      answers: data,
    };

    return lastValueFrom(this.http.put(url, body, httpOptions)).then(
      (response: any) => {
        return response;
      },
    );
  }

  loginLite(
    documentType: string,
    document: string,
    captcha3v: string,
  ): Observable<any> {
    // Rest token on login
    //this.token = undefined;
    // let captcha = environment.recaptcha.siteKey;
    const id = this.storageService.getSelectedMall().id;
    let captcha = captcha3v;
    let url = `${environment.url}/anubis/auth/login/lite/admin?customer=` + id;
    let body: any = {
      document: document,
      type_document: documentType,
    };

    if (captcha != null && captcha !== '') {
      body['captcha'] = captcha;
    }

    return this.http.post<any>(url, body, {});
  }

  getFlowById(token: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json; version=ClientesV1',
        Authorization: 'Bearer ' + window.sessionStorage.getItem('lite_token'),
      }),
    };
    let headers = this.storageService.getAuthHeaders();
    let url = `${environment.url}/horus/crm/profile/fields`;
    return this.http.get<Flow>(url, httpOptions).pipe(retry(2));
  }

  activateAdmin(token: any, data: any): Observable<any> {
    const httpOptions = this.storageService.getAuthHeaders();
    const url = `${environment.url}/anubis/auth/user/admin/activate`;
    return this.http.post(url, data, httpOptions);
  }

  sendTemporalCode(data: any) {
    const httpOptions = this.storageService.getAuthHeaders();
    const url = `${environment.url}/anubis/auth/temporal_code/admin`;
    return this.http.post(url, data, httpOptions).toPromise();
  }

  resendTemporalCode(data: any) {
    const httpOptions = this.storageService.getAuthHeaders();
    const url = `${environment.url}/anubis/auth/login/v2/resend-code`;
    return this.http.post(url, data, httpOptions).toPromise();
  }

  verifyTemporalCode(code: any) {
    const url = `${environment.url}/anubis/auth/login/admin/code`;
    const headers = this.storageService.getSessionAuthHeaders();
    const body = {
      code: code,
    };

    return this.http
      .post(url, body, headers)
      .toPromise()
      .then((data) => {
        return data;
      });
  }

  getAllClientsList() {
    let url = `${environment.url}/horus/crm/profile/list`;

    let params: any = {
      customer: this.storageService.getSelectedMall()?.id,
    };

    const headers = this.storageService.getSimpleAuthHeaders();
    return this.http.get(url, { params, headers }).toPromise();
  }

  updateDocumentTypeAnubis(id: number, data: any) {
    const url =
      environment.url + '/anubis/auth/user/' + id + '/change-document';
    const httpOptions = this.storageService.getAuthHeaders();
    return this.http.put(url, data, httpOptions).toPromise();
  }

  updateDocumentTypeHorus(id: number, data: any) {
    const url = environment.url + '/horus/crm/profile/' + id + '/document_type';
    const httpOptions = this.storageService.getAuthHeaders();
    return this.http.put(url, data, httpOptions).toPromise();
  }

  // getAllCitiesList() {
  //   let url = `${environment.url}/osiris/customer/cities/`;
  //   let headers = this.storageService.getAuthHeaders();

  //   return this.http.get(url, headers).toPromise();
  // }

  getAllCitiesList(search?: string) {
    let url = `${environment.url}/osiris/customer/cities/?limit=31956`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    console.debug('getAllCitiesList url', url);
    // return this.getAllPaginatedResults(url);
    let headers = this.storageService.getAuthHeaders();
    return this.http.get(url, headers).toPromise();
  }

  getNeighborhoodByCity(cityId: any) {
    let url = `${environment.url}/osiris/customer/cities/${cityId}/districts/?limit=10000`;
    console.debug('getNeighborhoodByCity url', url);
    // return this.getAllPaginatedResults(url);
    let headers = this.storageService.getAuthHeaders();
    return this.http.get(url, headers).toPromise();
  }

  getUserRolesAndLevels(): Promise<any> {
    let url = `${environment.url}/amun/core/user/roles-and-levels/`;
    let liteToken = window.sessionStorage.getItem('lite_token') as any;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + liteToken,
      }),
    };

    return this.http.get<any>(url, httpOptions).toPromise();
  }

  getUserFeaturedQuestions(): Promise<any> {
    let url = `${environment.url}/horus/crm/questions/featured/answers`;
    let httpOptions = this.storageService.getSessionAuthHeaders();

    return this.http.get<any>(url, httpOptions).toPromise();
  }

  /**
   * Obtiene los participantes únicos para la rueda de la fortuna
   * basado en los tickets de un concurso tipo Sorteo
   */
  async getParticipantsForWheel(campaignId: string): Promise<
    {
      user_id: number;
      name: string;
      tickets: number;
      document: string;
    }[]
  > {
    const headers = this.storageService.getAuthHeaders();

    // 1️⃣ Obtener tickets
    const tickets: any[] | undefined = await this.http
      .get<
        any[]
      >(`${environment.url}/thoth/campaigns/tickets/list-lite/${campaignId}`, headers)
      .toPromise();

    if (!tickets || tickets.length === 0) {
      throw new Error('NO_PARTICIPANTS');
    }

    // 2️⃣ Calcular usuarios únicos + conteo de tickets
    const ticketCountMap: Record<number, number> = {};
    const userIdsSet = new Set<number>();

    tickets.forEach((t) => {
      userIdsSet.add(t.user_id);
      ticketCountMap[t.user_id] = (ticketCountMap[t.user_id] || 0) + 1;
    });

    const userIds = Array.from(userIdsSet);

    // 3️⃣ Obtener perfiles desde Horus
    const profilesResponse: any = await this.getProfileInfoByIds(
      userIds.join(','),
      userIds.length,
      0,
    );

    const profiles = profilesResponse?.results || [];

    if (!profiles.length) {
      throw new Error('NO_VALID_PROFILES');
    }

    // 4️⃣ Construir pool final de participantes
    return profiles.map((profile: any) => ({
      user_id: profile.user_id,
      name: `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim(),
      tickets: ticketCountMap[profile.id] || 0,
      document: profile.document || '',
      id: profile.id,
    }));
  }

  async getReasonsByCustomer() {
    const customer = this.storageService.getSelectedMall().id;
    const info = await this.loadMall(customer);

    return info?.rejection_reasons || [];
  }

}
